import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/apiService';
import { Plus, Trash2, Settings } from 'lucide-react';

interface ApiKey {
  id: string;
  name: string;
  key?: string;
  isActive: boolean;
  usageCount: number;
  creditsUsed: number;
  lastUsedAt?: string;
  createdAt: string;
}

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);
  const [modelSelectionMode, setModelSelectionMode] = useState<'all' | 'custom_only' | 'global_only'>('all');

  useEffect(() => {
    loadApiKeys();
    loadUserPreferences();
  }, []);

  const loadUserPreferences = async () => {
    try {
      const response = await apiService.get('/portal/preferences');
      if (response?.apiModelPreferences?.modelSelectionMode) {
        setModelSelectionMode(response.apiModelPreferences.modelSelectionMode);
      }
    } catch (err) {
      console.error('Failed to load user preferences:', err);
    }
  };

  const handleSavePreferences = async () => {
    try {
      await apiService.put('/portal/preferences', {
        apiModelPreferences: {
          modelSelectionMode
        }
      });
      setShowPreferencesModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save preferences');
    }
  };

  const loadApiKeys = async () => {
    try {
      const keys = await apiService.getApiKeys();
      setApiKeys(keys);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;

    try {
      const result = await apiService.createApiKey(newKeyName.trim());
      setCreatedKey(result.key);
      setNewKeyName('');
      setShowCreateModal(false);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.revokeApiKey(keyId);
      await loadApiKeys();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const maskKey = (keyId: string) => {
    // Always show the key ID with masking - full keys are never returned after creation
    return `fxk_${keyId.substring(0, 8)}••••••••••••••••••••••••••••••••••••••••••••••••••••`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Keys</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your API keys for accessing FenxChat API
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreferencesModal(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Settings className="h-4 w-4 mr-2" />
            API Settings
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create API Key
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Created Key Modal */}
      {createdKey && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                API Key Created Successfully
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Copy your API key now. You won't be able to see it again.
              </p>
              <div className="bg-gray-50 p-3 rounded border">
                <code className="text-sm break-all">{createdKey}</code>
              </div>
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => copyToClipboard(createdKey)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Copy
                </button>
                <button
                  onClick={() => setCreatedKey(null)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Create New API Key
              </h3>
              <input
                type="text"
                placeholder="Enter key name"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <div className="mt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateKey}
                  disabled={!newKeyName.trim()}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Preferences Modal */}
      {showPreferencesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-6 border w-[500px] shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                API Settings
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Configure how your API keys interact with models
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model Selection Mode
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose which models your API keys can access when making requests
                  </p>
                  <select
                    value={modelSelectionMode}
                    onChange={(e) => setModelSelectionMode(e.target.value as 'all' | 'custom_only' | 'global_only')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Models (Global + My Custom Models)</option>
                    <option value="custom_only">My Custom Models Only</option>
                    <option value="global_only">Global Models Only</option>
                  </select>

                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-xs text-blue-800">
                      {modelSelectionMode === 'all' &&
                        'Your API will use both FenxChat global models and your custom endpoint models.'}
                      {modelSelectionMode === 'custom_only' &&
                        'Your API will ONLY use your custom endpoint models (models you\'ve added with your own API keys).'}
                      {modelSelectionMode === 'global_only' &&
                        'Your API will ONLY use FenxChat global models (excludes your custom models).'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowPreferencesModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        {apiKeys.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No API keys yet. Create your first one to get started.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {apiKeys.map((key) => (
              <li key={key.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium text-gray-900">{key.name}</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          key.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {key.isActive ? 'Active' : 'Revoked'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>Usage: {key.usageCount} requests</span>
                      <span>Cost: ${key.creditsUsed.toFixed(4)}</span>
                      <span>
                        Last used: {key.lastUsedAt
                          ? new Date(key.lastUsedAt).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                      <span>
                        Created: {new Date(key.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="ml-4">
                    {key.isActive && (
                      <button
                        onClick={() => handleRevokeKey(key.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
